import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../../settings/local_settings';
import { LocalStorage } from '../../utils/LocalStorage';
import { ReportHeader } from './ReportHeader';
import { ReportSummary } from './ReportSummary';
import { ReportBlocks } from './ReportBlocks';
import { ReportFeedbackArea } from './ReportFeedbackArea';
import { ReportFooter } from './ReportFooter';
import { Loading } from '../Loading';
import './ReportViewer.scss';

export function ReportViewer() {
    const { uuid } = useParams();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const isAuthenticated = !!LocalStorage.fetchUserToken();

    useEffect(() => {
        fetchReport();
    }, [uuid]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const token = LocalStorage.fetchUserToken();

            // Si el usuario está logueado, intentar con el endpoint autenticado primero
            if (token) {
                try {
                    const authResponse = await fetch(`${API_URL}reports/${uuid}/`, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    if (authResponse.ok) {
                        const authData = await authResponse.json();
                        if (authData.status === 'OK') {
                            setReport(authData.data);
                            return;
                        }
                    }
                    // Si 401/403 u otro error, caer al endpoint público
                } catch (_) {
                    // Error de red en auth, caer al endpoint público
                }
            }

            // Fallback: endpoint público (sin auth)
            const response = await fetch(`${API_URL}public/reports/${uuid}/`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    setError('El reporte no existe o ha expirado.');
                } else {
                    setError('Error al cargar el reporte.');
                }
                return;
            }

            const data = await response.json();
            if (data.status === 'OK') {
                setReport(data.data);
            } else {
                setError(data.error_message || 'Error al cargar el reporte.');
            }
        } catch (err) {
            setError('Error de conexion. Por favor, intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const { hasBusinessUnits, hasAIContent, dateRange } = useMemo(() => {
        if (!report?.blocks) return { hasBusinessUnits: false, hasAIContent: false, dateRange: null };
        const blocks = report.blocks;

        let minStart = null;
        let maxEnd = null;
        for (const b of blocks) {
            const dr = b.query_params?.date_range;
            if (dr?.startDate && (!minStart || dr.startDate < minStart)) minStart = dr.startDate;
            if (dr?.endDate && (!maxEnd || dr.endDate > maxEnd)) maxEnd = dr.endDate;
        }

        return {
            hasBusinessUnits: blocks.some(b => b.query_params?.data_source_type === 'BUSINESS_UNITS'),
            hasAIContent: blocks.some(b => b.ai_insight || b.type === 'text'),
            dateRange: minStart && maxEnd ? { startDate: minStart, endDate: maxEnd } : null
        };
    }, [report]);

    if (loading) {
        return (
            <div className="report-viewer-container">
                <div className="report-viewer-loading">
                    <Loading />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="report-viewer-container">
                <div className="report-viewer-error">
                    <div className="error-icon">
                        <i className="fa fa-exclamation-circle"></i>
                    </div>
                    <h2>Reporte no disponible</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="report-viewer-container">
            <div className="report-viewer">
                <ReportHeader
                    title={report.title}
                    generatedAt={report.generated_at}
                    branding={report.branding}
                    dateRange={dateRange}
                    hasBusinessUnits={hasBusinessUnits}
                    hasAIContent={hasAIContent}
                />

                <ReportSummary summary={report.summary} />

                <ReportBlocks
                    blocks={report.blocks || []}
                    reportId={uuid}
                    isAuthenticated={isAuthenticated}
                />

                {isAuthenticated && <ReportFeedbackArea reportId={uuid} />}

                <ReportFooter branding={report.branding} />
            </div>
        </div>
    );
}
